import React from 'react'

/** Single shimmer block with optional width/height. */
export function ShimmerBox({
  className = '',
  style = {},
}: {
  className?: string
  style?: React.CSSProperties
}) {
  return <div className={`shimmer ${className}`} style={style} />
}

/** Home page skeleton: header, search, categories, popular section, menu grid. */
export function HomeShimmer() {
  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="space-y-2">
              <ShimmerBox className="h-8 w-40" />
              <ShimmerBox className="h-4 w-56" />
            </div>
            <div className="flex gap-2">
              <ShimmerBox className="h-10 w-10 rounded-full" />
              <ShimmerBox className="h-10 w-10 rounded-full" />
            </div>
          </div>
        </div>
      </header>

      {/* Search */}
      <div className="max-w-7xl mx-auto px-4 py-4">
        <ShimmerBox className="h-12 w-full rounded-lg" />
      </div>

      {/* Category pills */}
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="flex gap-2 overflow-hidden">
          {[1, 2, 3, 4, 5].map((i) => (
            <ShimmerBox key={i} className="h-10 w-24 rounded-full shrink-0" />
          ))}
        </div>
      </div>

      {/* Popular section */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex items-center gap-2 mb-4">
          <ShimmerBox className="h-6 w-6 rounded" />
          <ShimmerBox className="h-6 w-32" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white rounded-lg shadow-sm overflow-hidden">
              <ShimmerBox className="w-full h-32 rounded-none" />
              <div className="p-3 space-y-2">
                <ShimmerBox className="h-4 w-3/4" />
                <ShimmerBox className="h-3 w-12" />
                <ShimmerBox className="h-3 w-full" />
                <ShimmerBox className="h-3 w-2/3" />
                <ShimmerBox className="h-5 w-20" />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Menu section */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <ShimmerBox className="h-6 w-28 mb-4" />
        <div className="grid grid-cols-2 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="bg-white rounded-lg shadow-sm overflow-hidden">
              <ShimmerBox className="w-full h-28 rounded-none" />
              <div className="p-3 space-y-2">
                <ShimmerBox className="h-4 w-3/4" />
                <ShimmerBox className="h-3 w-10" />
                <ShimmerBox className="h-3 w-full" />
                <ShimmerBox className="h-5 w-16" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

/** Item detail page skeleton: back, image, title, description, details, options, price, CTA. */
export function ItemDetailShimmer() {
  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-2">
            <ShimmerBox className="h-5 w-5 rounded" />
            <ShimmerBox className="h-5 w-16" />
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-6">
        <ShimmerBox className="w-full h-64 rounded-lg mb-6" />

        <div className="bg-white rounded-lg shadow-sm p-6 mb-6 space-y-4">
          <ShimmerBox className="h-9 w-3/4" />
          <ShimmerBox className="h-4 w-14" />
          <ShimmerBox className="h-4 w-full" />
          <ShimmerBox className="h-4 w-full" />
          <ShimmerBox className="h-4 w-3/4" />

          <div className="flex gap-4 pt-2">
            <ShimmerBox className="h-4 w-20" />
            <ShimmerBox className="h-4 w-24" />
            <ShimmerBox className="h-4 w-20" />
          </div>

          <div className="pt-4">
            <ShimmerBox className="h-4 w-24 mb-3" />
            <div className="grid grid-cols-2 gap-3">
              <ShimmerBox className="h-14 w-full rounded-lg" />
              <ShimmerBox className="h-14 w-full rounded-lg" />
            </div>
          </div>

          <div className="pt-2">
            <ShimmerBox className="h-4 w-20 mb-3" />
            <div className="space-y-2">
              <ShimmerBox className="h-12 w-full rounded-lg" />
              <ShimmerBox className="h-12 w-full rounded-lg" />
              <ShimmerBox className="h-12 w-2/3 rounded-lg" />
            </div>
          </div>

          <div className="pt-2">
            <ShimmerBox className="h-4 w-28 mb-3" />
            <ShimmerBox className="h-10 w-full rounded-lg" />
          </div>

          <div className="border-t pt-4 flex justify-between items-center">
            <ShimmerBox className="h-6 w-16" />
            <ShimmerBox className="h-8 w-28" />
          </div>

          <ShimmerBox className="h-14 w-full rounded-lg" />
        </div>
      </div>
    </div>
  )
}
